import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import * as partnerFileActions from 'features/partnerFile/actionCreators';
import PartnerFileListTopToolbar from './PartnerFileListTopToolbar';
import PartnerFileList from './PartnerFileList';
import HeaderPortal from 'components/layout/HeaderPortal';
import { Grid } from '@mui/material';
import GridOn from '@mui/icons-material/GridOn';
import { clean, getInt, objToQueryString } from 'utils/functions';
import useDebounce from 'utils/hooks/useDebounce';
import { Card } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { loadPartnerFileVisibilityLevels, loadStates, loadUser } from 'actions/actionCreators/dictionariesActions';
import { loadPartnerTypes } from 'features/offer/edit/client/form/actionCreators';
import documentTypeIds from 'constants/documentTypeIds';
import * as visibilityLevels from 'constants/visibilityLevels';

const DEBOUNCE_DELAY = 500;

const mandatoryFields = ['partnerName', 'fileNumber'];
const defaultPager = { page: 1, pageSize: 10, totalCount: 0 };

const blankFilters = {
  fullTextFilter: '',
  partnerName: null,
  uniqueId: null,
  fileNumber: null,
  partnerTypeId: null,
  userId: null,
  stateId: null,
  startDate: null,
  endDate: null,
  isActive: true,
  orderByColumnName: 'SortableFileNumber',
  orderByDescending: true,
  visibilityLevelId: visibilityLevels.MyDocuments
};

const PartnerFileListContainer = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const userPreferences = useSelector(state => state.partnerFiles.userPreferences, shallowEqual);
  const selectedUserPreference = useSelector(state => state.partnerFiles.selectedUserPreference, shallowEqual);
  const defaultUserPreference = useSelector(state => state.partnerFiles.defaultUserPreference, shallowEqual);

  const [partnerFiles, setPartnerFiles] = useState(null);
  const [filters, setFilters] = useState(null);
  const [pager, setPager] = useState(defaultPager);
  const [currentSelectedUserPreference, setCurrentSelectedUserPreference] = useState(null);

  const visibleFilters = currentSelectedUserPreference?.filters || selectedUserPreference.filters;
  const visibleFields = currentSelectedUserPreference?.fields || selectedUserPreference.fields;

  const debounceFiltersValueChanged = useDebounce(filters, DEBOUNCE_DELAY);

  const initCurrentPreference = newPreference => initFilters({ ...getFilterValuesFromUserPreference(newPreference), fullTextFilter: '' });

  const getFiltersFromURL = value => {
    const url = new URLSearchParams(value);
    return {
      fullTextFilter: url.get('fullTextFilter'),
      partnerName: url.get('partnerName'),
      uniqueId: url.get('uniqueId'),
      fileNumber: url.get('fileNumber'),
      partnerTypeId: getInt(url.get('partnerTypeId')),
      userId: getInt(url.get('userId')),
      stateId: getInt(url.get('stateId')),
      offerTypeId: getInt(url.get('offerTypeId')),
      startDate: url.get('startDate'),
      endDate: url.get('endDate'),
      visibilityLevelId: getInt(url.get('visibilityLevelId')),
      isActive: JSON.parse(url.get('isActive')),
      orderByDescending: JSON.parse(url.get('orderByDescending')),
      orderByColumnName: url.get('orderByColumnName')
    };
  };

  const getPagerFromURL = value => {
    const url = new URLSearchParams(value);
    return {
      page: getInt(url.get('page')) ?? defaultPager.page,
      pageSize: getInt(url.get('pageSize')) ?? defaultPager.pageSize,
      totalCount: defaultPager.totalCount
    };
  };

  const getFilterValuesFromUserPreference = useCallback(userPreference => {
    let newFilters = { ...blankFilters };
    if (!userPreference) return newFilters;

    userPreference.filters.forEach(el => {
      newFilters[el.fieldName] = el.defaultValue;
    });
    if (userPreference.order) {
      newFilters.orderByColumnName = userPreference.order.orderByColumnName;
      newFilters.orderByDescending = userPreference.order.orderByDescending;
    }

    return getFiltersFromURL(objToQueryString(newFilters));
  }, []);

  const filterPartnerFiles = useCallback(
    (searchFilters, searchPager) => {
      async function loadPartnerFilesAsync() {
        let partners = await dispatch(partnerFileActions.loadPartnerFiles(getQueryStringFromFiltersAndPaging(searchFilters, searchPager)));
        setPartnerFiles(partners);
      }

      loadPartnerFilesAsync();
    },
    [dispatch]
  );

  const getQueryStringFromFiltersAndPaging = (filters, pager) =>
    objToQueryString({
      ...filters,
      page: pager.page === defaultPager.page ? '' : pager.page,
      pageSize: pager.pageSize === defaultPager.pageSize ? '' : pager.pageSize
    });

  const pushFiltersAndPagingToQueryString = useCallback(
    (newFilters, newPager) => {
      if (!userPreferences.isLoaded) return;
      history.push('/partner-files?' + getQueryStringFromFiltersAndPaging(newFilters, newPager));
      filterPartnerFiles(newFilters, newPager);
    },
    [userPreferences.isLoaded, history, filterPartnerFiles]
  );

  const initFilters = useCallback(
    async receivedFilters => {
      const newFilters = { ...receivedFilters };

      if (newFilters.userId !== null && (!newFilters?.user || newFilters.userId !== newFilters?.user?.userId))
        newFilters.user = await dispatch(loadUser(newFilters.userId));

      if (
        newFilters.partnerTypeId !== null &&
        (!newFilters?.partnerType || newFilters.partnerTypeId !== newFilters?.partnerType?.partnerTypeId)
      )
        newFilters.partnerType = await dispatch(loadPartnerTypes()).then(data => {
          return data.find(d => d.partnerTypeId === newFilters.partnerTypeId);
        });

      if (newFilters.stateId !== null && (!newFilters?.state || newFilters.stateId !== newFilters?.state?.stateId))
        newFilters.state = await dispatch(loadStates(documentTypeIds.PartnerFile)).then(data => {
          return data.find(d => d.stateId === newFilters.stateId);
        });

      if (
        newFilters.visibilityLevelId !== null &&
        (!newFilters?.visibilityLevel || newFilters.visibilityLevelId !== newFilters?.visibilityLevel?.visibilityLevelId)
      )
        newFilters.visibilityLevel = await dispatch(loadPartnerFileVisibilityLevels()).then(data => {
          return data?.find(d => d.visibilityLevelId === newFilters.visibilityLevelId);
        });

      setFilters({ ...newFilters });
    },
    [dispatch]
  );

  const initUserPreference = useCallback(() => {
    if (!location.search) return;

    const matchedUserPreferences = userPreferences.filter(function (up) {
      const userPrefenceStringFilters = up.filters.reduce((map, obj) => {
        map[obj.fieldName] = obj.defaultValue;
        return map;
      }, {});

      clean(userPrefenceStringFilters);
      const queryStringFilters = getFiltersFromURL(location.search);
      clean(queryStringFilters);
      const matchedFilters = [];

      Object.keys(queryStringFilters).forEach(function (key) {
        if (key.startsWith('orderBy')) {
          Object.keys(up.order).forEach(function (orderKey) {
            if (orderKey === key && up.order[orderKey] === queryStringFilters[key]) matchedFilters.push(orderKey);
          });
        } else {
          up.filters.forEach(function (f) {
            if (f.fieldName === key && String(f.defaultValue) === String(queryStringFilters[key])) matchedFilters.push(f);
          });
        }
      });

      return matchedFilters.length === Object.keys(queryStringFilters).length;
    });

    if (!!matchedUserPreferences && matchedUserPreferences.length > 0)
      setCurrentSelectedUserPreference(userPreferences.find(up => up.filterName === matchedUserPreferences[0].filterName));
    else setCurrentSelectedUserPreference({ ...selectedUserPreference, filterName: '' });
  }, [location.search, selectedUserPreference, userPreferences]);

  useEffect(() => {
    dispatch(partnerFileActions.loadUserPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (!userPreferences.isLoaded || filters != null) return;
    let newFilters;
    if (location.search !== '') {
      setPager(getPagerFromURL(location.search));
      newFilters = getFiltersFromURL(location.search);
    } else newFilters = { ...getFilterValuesFromUserPreference(userPreferences.find(up => up.implicit) ?? defaultUserPreference) };

    initFilters({ ...newFilters, fullTextFilter: newFilters.fullTextFilter ?? '' });
  }, [
    userPreferences,
    defaultUserPreference,
    filters,
    location.search,
    getFilterValuesFromUserPreference,
    initFilters,
    initUserPreference
  ]);

  useEffect(() => {
    if (!debounceFiltersValueChanged) return;
    initUserPreference();
    pushFiltersAndPagingToQueryString(debounceFiltersValueChanged, pager);
  }, [debounceFiltersValueChanged, pager, pushFiltersAndPagingToQueryString, initUserPreference]);

  const handleResetFilters = () => initFilters({ ...blankFilters });
  const handleRefresh = () => filterPartnerFiles(filters, pager);
  const handleAdd = () => history.push('/partner-files/new');

  const handleEdit = (documentId, siteId) => {
    sessionStorage.setItem('PartnerFileURLFilters', location.search);
    return `/partner-files/${documentId}/${siteId}`;
  };
  const handleDownload = async fileType => {
    const queryStringFilters = getFiltersFromURL();
    clean(queryStringFilters);
    const fieldsToShow = selectedUserPreference?.fields.filter(x => x.isVisible === true);

    const event = await dispatch(partnerFileActions.exportPartnerFiles({ ...blankFilters, ...queryStringFilters, fileType }, fieldsToShow));
    window.location = dispatch(partnerFileActions.buildPartnerFileExportDownloadUrl(event.filePath, event.fileName));
  };

  const handleFilterValueChanged = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
      userId: name === 'user' ? value?.userId : filters.userId,
      partnerTypeId: name === 'partnerType' ? value?.partnerTypeId : filters.partnerTypeId,
      stateId: name === 'state' ? value?.stateId : filters.stateId,
      visibilityLevelId: name === 'visibilityLevel' ? value?.visibilityLevelId : filters.visibilityLevelId,
      offerTypeId: name === 'offerType' ? value?.offerTypeId : filters.offerTypeId
    });
  };

  const handleFilterVisibleValueChanged = (name, value) => {
    const filter = visibleFilters.find(x => x.fieldName === name);
    const newVisibleFilters = Object.assign([...visibleFilters], { [visibleFilters.indexOf(filter)]: { ...filter, isVisible: value } });
    setCurrentSelectedUserPreference({ ...currentSelectedUserPreference, filters: newVisibleFilters });
  };

  const handleFieldsVisibleValueChanged = (name, value) => {
    if (mandatoryFields.includes(name)) return;
    const filter = visibleFields.find(x => x.fieldName === name);
    const newVisibleFields = Object.assign([...visibleFields], { [visibleFields.indexOf(filter)]: { ...filter, isVisible: value } });
    if (!newVisibleFields.some(x => x.isVisible === true)) return;
    setCurrentSelectedUserPreference({
      ...currentSelectedUserPreference,
      ilterName: '',
      implicit: false,
      isDefault: false,
      fields: newVisibleFields
    });
  };

  const handleChangePage = pageChanged => setPager(prev => ({ ...prev, page: pageChanged + 1 }));
  const handleChangeRowsPerPage = pageSizeChanged => setPager(prev => ({ ...prev, pageSize: pageSizeChanged, page: 1 }));
  const handleUserPreferenceChanged = name => initCurrentPreference(userPreferences.find(x => x.filterName === name));

  const handleAddUserPreference = popUpUserPreference => {
    if (
      popUpUserPreference.filterName === '' ||
      userPreferences.some(x => x.filterName.toLowerCase() === popUpUserPreference.filterName.trim().toLowerCase())
    )
      return;
    const newPreference = {
      ...popUpUserPreference,
      isDefault: false,
      implicit: false,
      filterName: popUpUserPreference.filterName.trim(),
      filters: popUpUserPreference.filters.map(f => {
        return { ...f, defaultValue: filters[f.fieldName]?.toString() ?? '' };
      })
    };

    dispatch(partnerFileActions.updateUserPreferences([...userPreferences, newPreference]));
  };

  const handleDeleteUserPreference = () => {
    dispatch(
      partnerFileActions.updateUserPreferences(userPreferences.filter(p => p.filterName !== currentSelectedUserPreference.filterName))
    );
    const newPreference = {
      ...currentSelectedUserPreference,
      isDefault: false,
      implicit: false,
      filterName: '',
      filters: currentSelectedUserPreference.filters.map(f => {
        return { ...f, defaultValue: filters[f.fieldName]?.toString() ?? f.defaultValue };
      })
    };
    dispatch(partnerFileActions.updateSelectedUserPreference(newPreference));
    initCurrentPreference(newPreference);
  };

  const handleListDeleteChanged = item => {
    if (item.filterName === selectedUserPreference.filterName)
      setCurrentSelectedUserPreference({ ...currentSelectedUserPreference, isDefault: false, filterName: '', implicit: false });

    dispatch(partnerFileActions.updateUserPreferences(userPreferences.filter(p => p.filterName !== item.filterName)));
  };

  const handleListImplicitChanged = item => {
    const newUserPreferences = userPreferences.map(up => {
      return { ...up, implicit: up.filterName === item.filterName ? !item.implicit : false };
    });
    dispatch(partnerFileActions.updateUserPreferences(newUserPreferences));
    const newPreference = newUserPreferences.find(p => p.implicit === true);
    if (newPreference) {
      dispatch(partnerFileActions.updateSelectedUserPreference(newPreference));
      initCurrentPreference(newPreference);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12}>
        <Card icon={GridOn}>
          {debounceFiltersValueChanged && (
            <PartnerFileListTopToolbar
              filters={filters || { ...blankFilters }}
              onFilterValueChanged={handleFilterValueChanged}
              onFilterVisibleValueChanged={handleFilterVisibleValueChanged}
              onFieldsVisibleValueChanged={handleFieldsVisibleValueChanged}
              onResetFilters={handleResetFilters}
              onAdd={handleAdd}
              onDownload={handleDownload}
              downloadEnabled={partnerFiles?.totalCount > 0}
              visibleFilters={[...visibleFilters]}
              visibleFields={[...visibleFields]}
              userPreferences={userPreferences}
              selectedUserPreference={currentSelectedUserPreference || selectedUserPreference}
              onUserPreferenceChanged={handleUserPreferenceChanged}
              onAddUserPreference={handleAddUserPreference}
              onDeleteUserPreference={handleDeleteUserPreference}
              onListDeleteChanged={handleListDeleteChanged}
              onListImplicitChanged={handleListImplicitChanged}
            />
          )}

          {!partnerFiles ? (
            <WrappedFakeText my={2} />
          ) : (
            <PartnerFileList
              partnerFiles={partnerFiles.values}
              fieldsToShow={visibleFields.filter(x => x.isVisible).map(x => x.fieldName)}
              onEdit={handleEdit}
            />
          )}

          <PaginationWrapper
            count={partnerFiles?.totalCount ?? defaultPager.totalCount}
            pageSize={partnerFiles?.pageSize ?? defaultPager.pageSize}
            page={partnerFiles?.page ?? defaultPager.page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onPageChange={handleChangePage}
            onRefresh={handleRefresh}
          />
        </Card>
      </Grid>
      <HeaderPortal />
    </Grid>
  );
};

export default PartnerFileListContainer;
